import charbonnieresHero from "@/assets/villes/charbonnieres-hero.webp";
import charbonnieresInterieur from "@/assets/villes/charbonnieres-interieur.webp";
import dardillyHero from "@/assets/villes/dardilly-hero.webp";
import dardillyInterieur from "@/assets/villes/dardilly-interieur.webp";
import ecullyHero from "@/assets/villes/ecully-hero.webp";
import ecullyInterieur from "@/assets/villes/ecully-interieur.webp";
import caluireHero from "@/assets/villes/caluire-hero.webp";
import caluireInterieur from "@/assets/villes/caluire-interieur.webp";
import tassinHero from "@/assets/villes/tassin-hero.webp";
import tassinInterieur from "@/assets/villes/tassin-interieur.webp";
import oullinsHero from "@/assets/villes/oullins-hero.webp";
import oullinsInterieur from "@/assets/villes/oullins-interieur.webp";
import sainteFoyHero from "@/assets/villes/sainte-foy-hero.webp";
import sainteFoyInterieur from "@/assets/villes/sainte-foy-interieur.webp";
import brindasHero from "@/assets/villes/brindas-hero.webp";
import brindasInterieur from "@/assets/villes/brindas-interieur.webp";
import craponneHero from "@/assets/villes/craponne-hero.webp";
import craponneInterieur from "@/assets/villes/craponne-interieur.webp";

export interface LocalAreaFaq {
  question: string;
  answer: string;
}

export interface LocalArea {
  slug: string;
  city: string;
  cityShort: string;
  postalCode: string;
  geo: { lat: number; lng: number };
  intro: string;
  context: string;
  specificities: string[];
  examples: string;
  distance: string;
  population?: string;
  heroImage: string;
  heroAlt: string;
  heroCaption: string;
  interiorImage: string;
  interiorAlt: string;
  interiorCaption: string;
  neighborhoods: string[];
  housing: string;
  budget: string;
  faq: LocalAreaFaq[];
}

export const localAreas: LocalArea[] = [
  {
    slug: "architecte-interieur-charbonnieres-les-bains",
    city: "Charbonnières-les-Bains",
    cityShort: "Charbonnières-les-Bains",
    postalCode: "69260",
    geo: { lat: 45.7833, lng: 4.7333 },
    population: "5 200 habitants",
    distance: "10 min de Lyon",
    heroImage: charbonnieresHero,
    heroAlt: "Villa Belle Époque à Charbonnières-les-Bains, façade claire et volets bois",
    heroCaption: "Les villas de l'ancienne station thermale, typiques du haut de Charbonnières.",
    interiorImage: charbonnieresInterieur,
    interiorAlt: "Cuisine contemporaine installée sous les moulures d'une villa ancienne",
    interiorCaption: "Cuisine actuelle glissée sous des moulures d'origine, sans toucher au plafond.",
    intro: "Architecte d'intérieur à Charbonnières-les-Bains : aménagement, rénovation et décoration de villas, demeures bourgeoises et résidences contemporaines dans l'Ouest lyonnais.",
    context: "Ancienne station thermale, Charbonnières a gardé ses villas Belle Époque et ses grandes demeures du XIXe, auxquelles se sont ajoutées des maisons d'architecte plus récentes. Le sujet, ici, revient presque toujours au même point : comment installer une cuisine ou une salle d'eau actuelle sans abîmer une pièce qui a cent-vingt ans. Les hauteurs sous plafond aident, les réseaux électriques beaucoup moins.\n\nDans ces maisons, la contrainte n'est presque jamais la surface. Elle vient des planchers bois qui bougent, des cheminées condamnées à contourner, des fenêtres à petits bois qu'on ne remplacera pas. Je travaille donc en couches : on garde ce qui fait le caractère, on refait entièrement ce qui ne se voit pas, réseaux, isolation, ventilation.",
    housing: "Villas Belle Époque, demeures de maître, maisons d'architecte des années 2000, quelques copropriétés récentes autour du centre.",
    neighborhoods: ["Le centre et le parc thermal", "Le Bois", "La Verrière", "Les hauteurs vers Saint-Didier"],
    budget: "Rénovation partielle d'une villa : 900 à 1 400€/m². Reprise complète avec réseaux et menuiseries : 1 500 à 2 200€/m².",
    specificities: [
      "Rénovation de villas anciennes (charpente, hauteur sous plafond, moulures)",
      "Aménagement de cuisines ouvertes dans des espaces classés",
      "Création de salles de bains spa dans des dépendances",
      "Décoration intérieure haut de gamme avec mobilier sur mesure",
    ],
    examples: "Les demandes que je reçois à Charbonnières concernent le plus souvent des maisons familiales à reprendre entièrement, des extensions à raccorder à une bâtisse ancienne, ou des pièces sombres à rouvrir sur le jardin.",
    faq: [
      {
        question: "Peut-on ouvrir une cuisine dans une villa ancienne de Charbonnières ?",
        answer: "Presque toujours, mais rarement en cassant tout le mur. On vérifie d'abord si le mur est porteur, on regarde le sens des solives, puis on ouvre en gardant un jambage ou une poutre apparente. Le résultat est plus juste visuellement et le chantier reste raisonnable.",
      },
      {
        question: "Faut-il une déclaration de travaux ?",
        answer: "Pour un aménagement intérieur sans modification de façade, non. Dès qu'on touche aux ouvertures, aux volets ou à la toiture, une déclaration préalable est nécessaire en mairie. Je prépare les pièces graphiques du dossier.",
      },
      {
        question: "Intervenez-vous aussi sur la décoration seule ?",
        answer: "Oui. Certains clients de Charbonnières ont une maison en bon état et cherchent surtout une direction : couleurs, matières, mobilier, éclairage. C'est une mission plus courte, entre quatre et six semaines.",
      },
    ],
  },
  {
    slug: "architecte-interieur-dardilly",
    city: "Dardilly",
    cityShort: "Dardilly",
    postalCode: "69570",
    geo: { lat: 45.8167, lng: 4.7667 },
    population: "21 000 habitants",
    distance: "15 min de Lyon",
    heroImage: dardillyHero,
    heroAlt: "Maison contemporaine à Dardilly avec grande baie vitrée ouverte sur le jardin",
    heroCaption: "Sur le plateau de Dardilly, les maisons récentes s'ouvrent largement sur le jardin.",
    interiorImage: dardillyInterieur,
    interiorAlt: "Séjour ouvert avec baie vitrée et escalier vers l'étage dans une maison familiale",
    interiorCaption: "Séjour au rez-de-chaussée, chambres à l'étage : la lumière doit circuler entre les deux.",
    intro: "Architecte d'intérieur à Dardilly : aménagement et rénovation de maisons familiales, villas et espaces professionnels dans le Nord-Ouest lyonnais.",
    context: "Dardilly s'étire entre le village du bas et le plateau et ce dénivelé change la donne selon le quartier. On y trouve des pavillons des années 70 et 80, des villas plus cossues vers le plateau et pas mal de programmes neufs récents. La question revient souvent : comment répartir la lumière entre deux niveaux quand le séjour est au rez-de-chaussée et les chambres à l'étage, sans isoler la pièce de vie du jardin.\n\nL'autre particularité locale, c'est la présence de zones d'activité. Je suis régulièrement appelée pour des bureaux, des cabinets ou des locaux d'accueil, avec la même exigence que pour un logement : de l'acoustique, un éclairage confortable, des matériaux qui vieillissent bien.",
    housing: "Pavillons des années 70 et 80, villas de plateau, programmes neufs, locaux tertiaires en zone d'activité.",
    neighborhoods: ["Dardilly-le-Bas", "Le Plateau", "La Beffe", "Les Noyeraies"],
    budget: "Rafraîchissement complet : 600 à 900€/m². Rénovation lourde avec redistribution : 1 200 à 1 800€/m².",
    specificities: [
      "Rénovation de maisons familiales avec ouverture sur le jardin",
      "Réagencement de villas sur deux niveaux (séjour bas, chambres hautes)",
      "Aménagement d'espaces professionnels et locaux d'activité",
      "Conception de cuisines ouvertes et suites parentales",
    ],
    examples: "Une villa des années 80 à moderniser de part en part, un pavillon à ouvrir sur la terrasse, un local professionnel à réorganiser pour un cabinet : ce sont les chantiers qui reviennent sur la commune.",
    faq: [
      {
        question: "Comment gagner de la lumière dans un pavillon des années 80 ?",
        answer: "Trois leviers dans l'ordre : supprimer les cloisons qui bloquent la traversée, agrandir une seule ouverture bien choisie plutôt que trois petites, puis travailler les teintes et les revêtements. Le troisième point coûte peu et change beaucoup.",
      },
      {
        question: "Travaillez-vous pour des locaux professionnels à Dardilly ?",
        answer: "Oui, cabinets, bureaux et espaces d'accueil. La démarche est la même que pour un logement, avec en plus les questions d'accessibilité PMR et d'acoustique entre postes de travail.",
      },
      {
        question: "Quels sont les délais moyens ?",
        answer: "Comptez six à dix semaines d'études selon l'ampleur, puis trois à cinq mois de travaux pour une rénovation complète de maison. Le premier rendez-vous est gratuit et le devis part sous 48h.",
      },
    ],
  },
  {
    slug: "architecte-interieur-ecully",
    city: "Écully",
    cityShort: "Écully",
    postalCode: "69130",
    geo: { lat: 45.7745, lng: 4.7689 },
    population: "18 600 habitants",
    distance: "8 min de Lyon",
    heroImage: ecullyHero,
    heroAlt: "Maison des années 70 à Écully derrière un mur en pierre, rue calme bordée de cyprès",
    heroCaption: "Les rues résidentielles d'Écully, entre maisons des années 70 et jardins clos.",
    interiorImage: ecullyInterieur,
    interiorAlt: "Cuisine ouverte avec îlot central en bois clair et plan de travail sombre",
    interiorCaption: "Cuisine fermée transformée en pièce de vie : l'îlot remplace le mur.",
    intro: "Architecte d'intérieur à Écully : rénovation de maisons, réagencement d'appartements et aménagement de bureaux, à huit minutes de Lyon.",
    context: "Écully mélange les maisons individuelles des années 60 et 70, des copropriétés récentes et un tissu d'entreprises installé autour de l'EM Lyon et de Boiron. Beaucoup de logements y ont de belles surfaces mais des plans datés : cuisine fermée de neuf mètres carrés, couloir qui dessert tout, garage jamais utilisé pour une voiture. C'est souvent là qu'on trouve les mètres carrés manquants.\n\nMon premier réflexe sur ces maisons est de dessiner le plan existant avec les circulations réelles. Dans neuf cas sur dix, le couloir mange six à huit mètres carrés qu'on peut redistribuer entre l'entrée, une salle d'eau et un rangement, sans casser un seul mur porteur.",
    housing: "Maisons individuelles des années 60 et 70, copropriétés récentes, appartements du centre, bureaux d'entreprises.",
    neighborhoods: ["Le centre-bourg", "Le Pérollier", "Les Sources", "Vianney et le secteur EM Lyon"],
    budget: "Réagencement d'appartement : 700 à 1 100€/m². Rénovation de maison avec cuisine et salles d'eau : 1 200 à 1 700€/m².",
    specificities: [
      "Rénovation de maisons familiales avec optimisation des volumes",
      "Réagencement d'appartements en copropriété",
      "Aménagement de bureaux pour entreprises locales",
      "Conception de cuisines design et dressings sur mesure",
    ],
    examples: "À Écully, on me sollicite aussi bien pour reprendre une villa des années 70 que pour moderniser un appartement du centre ou réaménager des bureaux devenus trop bruyants.",
    faq: [
      {
        question: "Peut-on transformer un garage en pièce de vie ?",
        answer: "Oui, c'est fréquent à Écully. Les points à traiter sont l'isolation du sol et des murs, l'apport de lumière naturelle et le chauffage. Au-delà de vingt mètres carrés créés, une déclaration préalable est à déposer.",
      },
      {
        question: "Faut-il l'accord de la copropriété ?",
        answer: "Pour tout ce qui reste dans les murs privatifs, non. Dès qu'on touche à un mur porteur, à une façade ou aux colonnes techniques, un vote en assemblée générale est nécessaire. Je fournis les plans et la note descriptive.",
      },
      {
        question: "Intervenez-vous sur des bureaux ?",
        answer: "Oui, des plateaux de bureaux et des espaces d'accueil pour les entreprises du secteur. Le sujet principal est presque toujours l'acoustique, suivi de la lumière.",
      },
    ],
  },
  {
    slug: "architecte-interieur-caluire-et-cuire",
    city: "Caluire-et-Cuire",
    cityShort: "Caluire",
    postalCode: "69300",
    geo: { lat: 45.795, lng: 4.85 },
    population: "43 800 habitants",
    distance: "5 min de Lyon",
    heroImage: caluireHero,
    heroAlt: "Maisons de ville en surplomb de la Saône à Caluire-et-Cuire, lumière de fin de journée",
    heroCaption: "Sur le coteau de Caluire, l'orientation décide de tout dans un projet.",
    interiorImage: caluireInterieur,
    interiorAlt: "Loft avec mezzanine métallique, brique apparente et grandes verrières",
    interiorCaption: "Un ancien atelier reconverti : la mezzanine récupère la hauteur disponible.",
    intro: "Architecte d'intérieur à Caluire-et-Cuire : rénovation d'appartements, maisons de ville et lofts au nord de Lyon.",
    context: "Caluire domine la Saône et cette position change tout : selon l'orientation, un séjour reçoit la lumière du soir en plein ou reste dans l'ombre du coteau dès seize heures. Le bâti va de la maison de ville ancienne à l'atelier industriel reconverti, en passant par des copropriétés des années 2000 dont les plans se ressemblent tous.\n\nJe commence donc toujours par une visite à l'heure où vous occupez vraiment la pièce. C'est ce qui détermine où placer le coin repas, où mettre un miroir et s'il faut ou non compenser avec un éclairage indirect en hauteur.",
    housing: "Maisons de ville anciennes, appartements bourgeois, ateliers reconvertis en lofts, copropriétés des années 2000.",
    neighborhoods: ["Caluire centre", "Cuire-le-Bas", "Montessuy", "Saint-Clair et les quais"],
    budget: "Rénovation d'appartement : 800 à 1 300€/m². Loft ou maison de ville reprise entièrement : 1 400 à 2 000€/m².",
    specificities: [
      "Rénovation d'appartements anciens avec préservation des moulures",
      "Conversion de lofts dans d'anciens bâtiments industriels",
      "Aménagement de maisons de ville avec jardin",
      "Création d'extensions et de surélévations",
    ],
    examples: "Les projets caluirards tournent souvent autour de trois demandes : ouvrir une cuisine, exploiter une hauteur sous plafond avec une mezzanine, ou reconnecter une maison de ville à son bout de jardin.",
    faq: [
      {
        question: "Une mezzanine est-elle possible chez moi ?",
        answer: "Il faut au minimum 4,20 m sous plafond pour obtenir deux niveaux vivables et une structure capable de reprendre la charge. Je vérifie ces deux points dès la première visite, avant de dessiner quoi que ce soit.",
      },
      {
        question: "Comment traiter un séjour orienté nord sur le coteau ?",
        answer: "On travaille les surfaces claires mais légèrement chaudes, on évite le blanc pur qui vire au gris et on installe plusieurs sources lumineuses basses plutôt qu'un plafonnier central. L'effet est immédiat en fin de journée.",
      },
      {
        question: "Suivez-vous le chantier ?",
        answer: "Oui, si vous le souhaitez. Je consulte les entreprises, je compare les devis et je passe sur place aux étapes clés, avec un compte rendu écrit après chaque visite.",
      },
    ],
  },
  {
    slug: "architecte-interieur-tassin-la-demi-lune",
    city: "Tassin-la-Demi-Lune",
    cityShort: "Tassin",
    postalCode: "69160",
    geo: { lat: 45.7625, lng: 4.7825 },
    population: "23 500 habitants",
    distance: "10 min de Lyon",
    heroImage: tassinHero,
    heroAlt: "Rue commerçante de Tassin-la-Demi-Lune avec devanture en bois sombre",
    heroCaption: "Le tissu commerçant de Tassin : des locaux souvent étroits et profonds.",
    interiorImage: tassinInterieur,
    interiorAlt: "Intérieur de boutique en longueur avec étagères en bois et suspensions chaudes",
    interiorCaption: "Local en longueur : l'éclairage rythme la profondeur et guide le regard.",
    intro: "Architecte d'intérieur à Tassin-la-Demi-Lune : aménagement et rénovation pour particuliers et professionnels dans l'Ouest lyonnais.",
    context: "Entre les lotissements familiaux, les programmes neufs et le tissu commercial autour de Tassin La Bourse, la commune a deux visages. Côté logement, on me demande surtout d'optimiser des maisons qui ont vieilli ; côté commerce, d'installer une boutique ou un cabinet dans un local souvent étroit et profond.\n\nCes locaux en longueur ont tous le même défaut : la lumière s'arrête à trois mètres de la vitrine. On y répond avec un plan en séquences, des matières plus claires au fond et un éclairage pensé zone par zone plutôt qu'en nappe uniforme.",
    housing: "Lotissements familiaux, maisons des années 80, appartements neufs en VEFA, locaux commerciaux de centre-ville.",
    neighborhoods: ["La Demi-Lune", "Tassin centre et Le Bourg", "Alaï", "Les Pierres Blanches"],
    budget: "Personnalisation d'un logement neuf : 400 à 800€/m². Agencement d'un local commercial : 700 à 1 500€/m².",
    specificities: [
      "Aménagement de maisons individuelles familiales",
      "Personnalisation d'appartements en VEFA (programmes neufs)",
      "Architecture commerciale (boutiques, salons, cabinets)",
      "Rénovation énergétique avec amélioration du confort",
    ],
    examples: "Une maison des années 80 à remettre au goût du jour, un local commercial à agencer, un appartement neuf livré brut à personnaliser avant l'emménagement : ce sont les trois cas de figure les plus fréquents ici.",
    faq: [
      {
        question: "Quand intervenir sur un appartement acheté en VEFA ?",
        answer: "Le plus tôt possible, idéalement avant la clôture des travaux modificatifs acquéreur. On peut alors déplacer une cloison ou une arrivée d'eau pour un coût dérisoire comparé à une reprise après livraison.",
      },
      {
        question: "Aménagez-vous des commerces et cabinets ?",
        answer: "Oui, boutiques, salons et cabinets libéraux. Je prends en charge le plan d'agencement, les matériaux, l'éclairage, la signalétique et les contraintes d'accessibilité liées aux établissements recevant du public.",
      },
      {
        question: "Peut-on faire une mission courte ?",
        answer: "Oui. Une mission de conseil sur une demi-journée, avec compte rendu et pistes chiffrées, suffit parfois à débloquer un projet.",
      },
    ],
  },
  {
    slug: "architecte-interieur-oullins",
    city: "Oullins",
    cityShort: "Oullins",
    postalCode: "69600",
    geo: { lat: 45.7167, lng: 4.8167 },
    population: "26 200 habitants",
    distance: "10 min de Lyon",
    heroImage: oullinsHero,
    heroAlt: "Maisons de ville anciennes à Oullins avec un immeuble récent en arrière-plan",
    heroCaption: "À Oullins, l'ancien et le neuf se côtoient rue après rue.",
    interiorImage: oullinsInterieur,
    interiorAlt: "Petit séjour optimisé avec menuiserie sur mesure intégrant bureau et rangements",
    interiorCaption: "Sur 65 m², le sur-mesure crée un bureau sans sacrifier le séjour.",
    intro: "Architecte d'intérieur à Oullins : rénovation de maisons, appartements et locaux professionnels au sud de Lyon.",
    context: "Depuis l'arrivée du métro B, Oullins a changé de public : beaucoup de primo-accédants, des budgets travaux comptés, des biens achetés justement parce qu'ils demandaient du travail. On y trouve des maisons de ville avec un mouchoir de jardin, des copropriétés anciennes le long du Rhône et du neuf autour de la gare.\n\nSur ces projets, la valeur ajoutée se joue au centimètre : une porte déplacée de trente centimètres libère un mur entier, une menuiserie sur mesure remplace trois meubles achetés. Je chiffre systématiquement deux scénarios, un resserré et un plus ambitieux, pour que l'arbitrage vous appartienne.",
    housing: "Maisons de ville avec jardinet, copropriétés anciennes, immeubles neufs autour de la gare et du métro.",
    neighborhoods: ["Oullins centre", "La Saulaie", "Le Golf", "Les Célestins"],
    budget: "Rénovation d'un T3 ancien : 700 à 1 200€/m². Maison de ville reprise entièrement : 1 100 à 1 600€/m².",
    specificities: [
      "Rénovation de maisons de ville et leurs jardins",
      "Modernisation d'appartements anciens",
      "Personnalisation d'appartements neufs (VEFA)",
      "Aménagement de locaux pour TPE et professions libérales",
    ],
    examples: "Ici, la question est souvent la même : comment tirer une chambre, un bureau ou une salle d'eau supplémentaires d'une surface qui ne bougera pas. Un T3 de 65 m² bien redistribué vaut mieux qu'un déménagement.",
    faq: [
      {
        question: "Peut-on créer une chambre supplémentaire sans agrandir ?",
        answer: "Souvent oui, en récupérant un couloir, une partie de séjour trop généreuse ou une cuisine surdimensionnée. La vraie limite est la lumière naturelle : une pièce sans fenêtre ne peut pas être qualifiée de chambre.",
      },
      {
        question: "Un architecte d'intérieur est-il rentable sur un petit budget ?",
        answer: "C'est justement là que la mission se rentabilise le plus. Éviter une cloison inutile ou un mauvais achat de mobilier couvre souvent les honoraires. Je le vérifie chiffres à l'appui dès la proposition.",
      },
      {
        question: "Travaillez-vous avec des artisans du secteur ?",
        answer: "Oui, un réseau d'entreprises du sud et de l'ouest lyonnais avec lesquelles je travaille régulièrement. Vous restez libre de retenir vos propres artisans.",
      },
    ],
  },
  {
    slug: "architecte-interieur-sainte-foy-les-lyon",
    city: "Sainte-Foy-lès-Lyon",
    cityShort: "Sainte-Foy",
    postalCode: "69110",
    geo: { lat: 45.74, lng: 4.8 },
    population: "21 800 habitants",
    distance: "10 min de Lyon",
    heroImage: sainteFoyHero,
    heroAlt: "Grande maison familiale à Sainte-Foy-lès-Lyon avec extension vitrée sur jardin",
    heroCaption: "Les maisons foréziennes offrent souvent de la surface et un vrai jardin.",
    interiorImage: sainteFoyInterieur,
    interiorAlt: "Vaste pièce de réception avec extension verre et acier ouverte sur le parc",
    interiorCaption: "Une extension verre et acier prolonge la pièce de réception vers le jardin.",
    intro: "Architecte d'intérieur à Sainte-Foy-lès-Lyon : aménagement de maisons familiales, rénovation et décoration intérieure dans l'Ouest lyonnais.",
    context: "Sainte-Foy, ce sont surtout des maisons avec jardin, parfois de grandes surfaces héritées et rarement retouchées depuis leur construction. Les projets y sont d'ampleur : on reprend la distribution complète, on ouvre sur l'extérieur, on récupère un sous-sol enterré qui ne servait qu'au stockage.\n\nSur des surfaces de 200 à 400 m², le risque n'est pas le manque de place mais la dispersion. Je travaille par pôles, jour, nuit, réception, bien-être, avec des transitions nettes entre eux. Une grande maison réussie se reconnaît à ses circulations, pas à ses mètres carrés.",
    housing: "Maisons familiales de 150 à 400 m², propriétés avec parc, quelques copropriétés de standing.",
    neighborhoods: ["Le Bourg", "Chavril", "La Gravière", "Beaunant et le vallon de l'Yzeron"],
    budget: "Rénovation complète d'une grande maison : 1 300 à 2 000€/m². Extension neuve : 2 200 à 3 000€/m².",
    specificities: [
      "Rénovation complète de maisons familiales (200 à 400 m²)",
      "Conception d'extensions contemporaines",
      "Aménagement de cuisines ouvertes et espaces de réception",
      "Création de suites parentales et chambres d'enfants évolutives",
    ],
    examples: "Extension verre et acier sur une maison de plusieurs centaines de mètres carrés, cuisine ouverte prolongée par une terrasse, sous-sol transformé en espace sport ou bien-être : ce sont les chantiers typiques du secteur.",
    faq: [
      {
        question: "Peut-on aménager un sous-sol enterré ?",
        answer: "Oui, à condition de traiter l'humidité, la ventilation et l'apport de lumière. Une courette anglaise ou un puits de lumière change complètement l'usage de la pièce. Sans ces trois points, le résultat déçoit toujours.",
      },
      {
        question: "Travaillez-vous avec un architecte pour les extensions ?",
        answer: "Oui. Au-delà de 150 m² de surface totale, le recours à un architecte inscrit à l'Ordre est obligatoire pour le permis. Je conçois l'intérieur et je travaille en binôme avec lui sur l'enveloppe.",
      },
      {
        question: "Comment se déroule la première rencontre ?",
        answer: "Une visite d'une heure sur place, gratuite et sans engagement. On parle usages, contraintes et budget et vous recevez une proposition détaillée sous 48h.",
      },
    ],
  },
  {
    slug: "architecte-interieur-brindas",
    city: "Brindas",
    cityShort: "Brindas",
    postalCode: "69126",
    geo: { lat: 45.73, lng: 4.665 },
    population: "10 200 habitants",
    distance: "20 min de Lyon",
    heroImage: brindasHero,
    heroAlt: "Ferme en pierre dorée et grange à Brindas, dans les Monts du Lyonnais",
    heroCaption: "La pierre dorée des Monts du Lyonnais, très présente autour de Brindas.",
    interiorImage: brindasInterieur,
    interiorAlt: "Séjour aménagé dans une grange rénovée, mur en pierre et charpente apparente",
    interiorCaption: "Grange transformée en pièce de vie : la charpente reste le point fort.",
    intro: "Architecte d'intérieur à Brindas : aménagement et rénovation de maisons individuelles et propriétés rurales dans l'Ouest lyonnais.",
    context: "Brindas est la commune voisine de Grézieu-la-Varenne, où se trouve mon atelier : j'y suis en dix minutes, ce qui simplifie les visites de chantier. Le bâti mêle maisons individuelles récentes et anciennes fermes en pierre, avec les questions que cela pose : murs épais qui gardent le froid, ouvertures étroites, granges attenantes à réhabiliter.\n\nAvec la pierre, l'erreur classique est l'isolation intérieure généralisée : on coupe le mur de la pièce, on crée de la condensation et on perd le seul élément qui donnait du caractère. Je préfère isoler par zones, garder un ou deux pans apparents et travailler la chaux, qui laisse respirer le support.",
    housing: "Fermes et longères en pierre dorée, granges à réhabiliter, maisons individuelles récentes avec terrain.",
    neighborhoods: ["Le bourg", "Le Vieux Brindas", "Les hameaux vers Vaugneray", "Secteur Grézieu-la-Varenne"],
    budget: "Rénovation d'une maison récente : 700 à 1 100€/m². Réhabilitation d'une ferme ou d'une grange : 1 500 à 2 300€/m².",
    specificities: [
      "Rénovation de fermes et longères en pierre",
      "Aménagement de maisons familiales contemporaines",
      "Création de pool houses, vérandas et espaces extérieurs",
      "Décoration intérieure mêlant matières naturelles et design contemporain",
    ],
    examples: "Une longère du XIXe à rénover sans dénaturer la pierre, une grange à transformer en pièce de vie, une maison récente à réchauffer avec du bois et de la chaux : les projets du secteur se ressemblent peu, le terrain commande.",
    faq: [
      {
        question: "Comment isoler une maison en pierre sans l'abîmer ?",
        answer: "En laissant le mur respirer : enduits à la chaux, isolants perspirants et surtout une ventilation correcte. On évite le polystyrène collé, qui piège l'humidité dans la maçonnerie.",
      },
      {
        question: "Une grange peut-elle devenir une pièce de vie ?",
        answer: "Oui, c'est un des plus beaux exercices. Les sujets à régler sont le sol, souvent en terre battue, la charpente à conforter et l'apport de lumière, généralement par la grande ouverture existante plutôt que par de nouveaux percements.",
      },
      {
        question: "Vous déplacez-vous sur les communes voisines ?",
        answer: "Oui, mon atelier est à Grézieu-la-Varenne, à dix minutes. J'interviens sur Vaugneray, Craponne, Messimy et tout l'ouest lyonnais sans frais de déplacement supplémentaires.",
      },
    ],
  },
  {
    slug: "architecte-interieur-craponne",
    city: "Craponne",
    cityShort: "Craponne",
    postalCode: "69290",
    geo: { lat: 45.7456, lng: 4.7222 },
    population: "11 500 habitants",
    distance: "15 min de Lyon",
    heroImage: craponneHero,
    heroAlt: "Rue de Craponne bordée de maisons anciennes en pierre et de murs de jardin",
    heroCaption: "Le vieux Craponne, où les maisons de bourg côtoient les lotissements des années 70.",
    interiorImage: craponneInterieur,
    interiorAlt: "Séjour ouvert sur le jardin dans une maison familiale rénovée à Craponne",
    interiorCaption: "Maison des années 70 rouverte sur le jardin : la baie change tout le séjour.",
    intro: "Architecte d'intérieur à Craponne : rénovation de maisons familiales, extensions et réaménagement d'appartements, à quinze minutes de Lyon.",
    context: "Craponne est à dix minutes de mon atelier de Grézieu-la-Varenne, je connais bien ses rues et ses artisans. La commune s'est construite en deux temps : un bourg ancien resserré autour de l'église, puis des vagues de pavillons et de petites copropriétés à partir des années 60. Les demandes suivent la même logique, d'un côté des maisons de bourg sombres à ouvrir, de l'autre des pavillons cloisonnés à remettre au goût du jour.\n\nSur les pavillons, le sujet est presque toujours le même : une cuisine fermée, un séjour tourné vers la rue et un jardin qu'on ne voit pas. Je commence par regarder ce qu'on peut abattre sans toucher au porteur, puis je réoriente les pièces de vie vers le sud du terrain.",
    housing: "Maisons de bourg en pierre, pavillons des années 60 à 90 avec terrain, petites copropriétés récentes le long de l'avenue Édouard Millaud.",
    neighborhoods: ["Le centre bourg", "Le Chêne", "Les Vernes", "Avenue Édouard Millaud", "Secteur Presqu'île / Charbonnières"],
    budget: "Rafraîchissement d'un appartement : 550 à 850€/m². Rénovation lourde d'un pavillon avec cuisine et salles d'eau : 1 100 à 1 700€/m².",
    specificities: [
      "Ouverture de cuisines fermées dans les pavillons des années 70",
      "Réorientation des pièces de vie vers le jardin",
      "Rénovation de maisons de bourg en pierre",
      "Aménagement de combles et de sous-sols en pièces habitables",
    ],
    examples: "Un pavillon de 1975 dont on ouvre la cuisine et on agrandit la baie côté jardin, une maison de bourg à éclaircir sans perdre ses murs épais, un T3 récent à réorganiser pour un bureau : le secteur mélange les deux époques.",
    faq: [
      {
        question: "Peut-on ouvrir la cuisine d'un pavillon des années 70 ?",
        answer: "Presque toujours, oui. Il faut vérifier si le mur est porteur, ce qui se voit vite sur les plans ou par sondage. Si c'est le cas, on passe par une poutre avec étude structure, ce qui reste courant et chiffrable dès l'avant-projet.",
      },
      {
        question: "Intervenez-vous aussi sur les appartements à Craponne ?",
        answer: "Oui, sur les copropriétés le long de l'avenue Édouard Millaud et autour du centre. Les travaux touchant les parties communes ou les façades demandent l'accord de la copropriété, que je prépare avec vous en amont.",
      },
      {
        question: "À quelle distance êtes-vous de Craponne ?",
        answer: "Mon atelier est à Grézieu-la-Varenne, à dix minutes en voiture. Les visites de chantier se font sans frais de déplacement et je peux passer facilement quand une décision se prend sur place.",
      },
    ],
  },
];

export const findAreaBySlug = (slug: string) => localAreas.find((a) => a.slug === slug);
